import { IStatusPill } from 'types/ui/statusPill.interface'
import { IHRNotificationResponse } from 'types/api/hrDashboardApi.interface'
import { HRNotificationType, HrRequestStatus } from 'types/hrEnums.interface'
import { timeAgo } from 'utils/helpers'

export interface IHrNotificationDisplayRow {
  id: string
  title: string
  meta: string
  pillLabel: string
  pillTone: IStatusPill['status']
  type: number
}

const VALID_TYPES = new Set<number>([
  HRNotificationType.ContractExpiration,
  HRNotificationType.AccommodationGuestDurationExpiration,
  HRNotificationType.Birthday,
  HRNotificationType.AccommodationCheckInExpiration,
  HRNotificationType.ContractTermination,
  HRNotificationType.HrRequestSubmitted,
  HRNotificationType.DocumentExpiration,
  HRNotificationType.MissingWorkSchedule,
])

export const isDisplayableHrNotification = (n: IHRNotificationResponse): boolean => {
  if (!VALID_TYPES.has(Number(n.type))) return false
  if (n.type === HRNotificationType.AccommodationGuestDurationExpiration && !n.accommodationGuest) {
    return false
  }
  return Boolean(n.message && n.message.trim())
}

export const getHrNotificationPill = (
  n: IHRNotificationResponse,
): { label: string; tone: IStatusPill['status'] } => {
  switch (n.type) {
    case HRNotificationType.ContractExpiration:
      return { label: 'Contract', tone: 'warning' }
    case HRNotificationType.AccommodationGuestDurationExpiration:
      return { label: 'Guest', tone: 'warning' }
    case HRNotificationType.Birthday:
      return { label: 'Birthday', tone: 'info' }
    case HRNotificationType.AccommodationCheckInExpiration:
      return { label: 'Check-in', tone: 'warning' }
    case HRNotificationType.ContractTermination:
      return { label: 'Termination', tone: 'danger' }
    case HRNotificationType.HrRequestSubmitted: {
      const pending = n.hrRequest?.status === HrRequestStatus.Pending
      return { label: 'Request', tone: pending ? 'accent' : 'neutral' }
    }
    case HRNotificationType.DocumentExpiration:
      return { label: 'Document', tone: 'warning' }
    case HRNotificationType.MissingWorkSchedule:
      return { label: 'Schedule', tone: 'danger' }
    default:
      return { label: 'Alert', tone: 'neutral' }
  }
}

export const mapHrNotificationsToRows = (
  data: IHRNotificationResponse[],
  limit = 8,
): IHrNotificationDisplayRow[] => {
  return data
    .filter(isDisplayableHrNotification)
    .slice(0, limit)
    .map((n) => {
      const pill = getHrNotificationPill(n)
      return {
        id: String(n.id),
        title: n.message as string,
        meta: n.createdAt ? timeAgo(n.createdAt) || 'just now' : '',
        pillLabel: pill.label,
        pillTone: pill.tone,
        type: Number(n.type),
      }
    })
}
